import type { Thread } from '../services/chatService';

const LEGACY_BOT_TOGGLE_REASON = 'manual_operator_handoff';

function toCount(value: unknown): number {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

export function getThreadUnreadCount(thread: Thread): number {
  const raw = thread as Thread & {
    unread_count?: number;
    unread_client_count?: number;
    unread_bot_count?: number;
  };
  const perKind =
    toCount(raw.unreadClientCount ?? raw.unread_client_count) +
    toCount(raw.unreadBotCount ?? raw.unread_bot_count);
  const total = toCount(raw.unreadCount ?? raw.unread_count);
  return Math.max(total, perKind, raw.unread ? 1 : 0);
}

export function getThreadClientUnreadCount(thread: Thread): number {
  const raw = thread as Thread & {
    unread_count?: number;
    unread_client_count?: number;
    unread_bot_count?: number;
  };
  const explicitClient = raw.unreadClientCount ?? raw.unread_client_count;
  if (explicitClient !== undefined && explicitClient !== null) {
    return toCount(explicitClient);
  }

  const explicitBot = raw.unreadBotCount ?? raw.unread_bot_count;
  const total = getThreadUnreadCount(thread);
  return explicitBot === undefined || explicitBot === null
    ? total
    : Math.max(0, total - toCount(explicitBot));
}

/**
 * A bot being unavailable is not itself a human handoff. Older bot-toggle
 * endpoints stored `manual_operator_handoff`; treat that value as legacy bot
 * availability state so existing rows stop producing false yellow alerts.
 */
export function isHumanIntervention(thread: Thread): boolean {
  const reason = String(thread.escalation_reason || '').trim().toLowerCase();
  return thread.escalated_to_human === true && reason !== LEGACY_BOT_TOGGLE_REASON;
}

export function hasEffectiveBot(
  thread: Thread,
  channelDefaultBotId: number | null | undefined = undefined,
): boolean {
  if (thread.bot_enabled === false) return false;
  if (thread.selected_bot_id != null) return true;
  // Until the channel-default query settles, avoid flashing a false botless
  // alert for every thread that relies on that default.
  return channelDefaultBotId === undefined || channelDefaultBotId !== null;
}

export function isBotlessThread(
  thread: Thread,
  channelDefaultBotId: number | null | undefined = undefined,
): boolean {
  return !isHumanIntervention(thread) && !hasEffectiveBot(thread, channelDefaultBotId);
}

export interface ChatNotificationSummary {
  regularUnreadMessages: number;
  botlessUnreadMessages: number;
  humanInterventionThreads: number;
  appointmentThreads: number;
}

/**
 * Keep each notification category disjoint:
 * - regular unread messages retain the existing red badge;
 * - unread customer messages with no effective bot use orange;
 * - real handoffs use yellow and count threads, not messages.
 */
export function summarizeChatNotifications(
  threads: Thread[] | null | undefined,
  totalUnreadMessages: number,
  channelDefaultBotId: number | null | undefined = undefined,
): ChatNotificationSummary {
  const list = Array.isArray(threads) ? threads : [];
  let loadedUnreadMessages = 0;
  let botlessUnreadMessages = 0;
  let humanInterventionThreads = 0;
  let appointmentThreads = 0;

  for (const thread of list) {
    loadedUnreadMessages += getThreadUnreadCount(thread);
    if (isHumanIntervention(thread)) humanInterventionThreads += 1;
    if (thread.appointment_booked === true) appointmentThreads += 1;
    if (isBotlessThread(thread, channelDefaultBotId)) {
      botlessUnreadMessages += getThreadClientUnreadCount(thread);
    }
  }

  const unreadMessages = Math.max(toCount(totalUnreadMessages), loadedUnreadMessages);
  return {
    regularUnreadMessages: Math.max(0, unreadMessages - botlessUnreadMessages),
    botlessUnreadMessages,
    humanInterventionThreads,
    appointmentThreads,
  };
}
